import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  HouseHeart,
  Sofa,
  Wifi,
  Monitor,
  UtensilsCrossed,
  Clock,
  Sparkles,
  ClipboardList,
  Home,
  CheckCircle,
  Send,
  Menu,
  X
} from 'lucide-react';

const BookPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    moveInDate: '',
    lengthOfStay: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = encodeURIComponent(`Stay Inquiry - ${formData.fullName}`);
    const body = encodeURIComponent(
      `Name: ${formData.fullName}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Desired Location: ${formData.location}\n` +
      `Move-in Date: ${formData.moveInDate}\n` +
      `Length of Stay: ${formData.lengthOfStay}\n` +
      `Additional Notes: ${formData.notes || 'None'}`
    );
    
    window.location.href = `mailto:hello@envoystays.com?subject=${subject}&body=${body}`;
  };

  const amenities = [
    { icon: Sofa, title: 'Fully Furnished', description: 'Quality furniture, linens, and everything you need from day one' },
    { icon: Wifi, title: 'High-Speed WiFi', description: 'Reliable internet for remote work and streaming' },
    { icon: Monitor, title: 'Dedicated Workspace', description: 'Comfortable desk setup for productive workdays' },
    { icon: UtensilsCrossed, title: 'Full Kitchen', description: 'Fully equipped kitchen with appliances and cookware' },
    { icon: Clock, title: 'Flexible Check-In', description: 'Self-service smart lock access on your schedule' },
    { icon: Sparkles, title: 'Professional Cleaning', description: 'Spotless unit ready for your arrival' }
  ];

  const steps = [
    { number: '1', icon: ClipboardList, title: 'Submit Your Request', description: 'Tell us your dates, location preference, and any specific needs' },
    { number: '2', icon: Home, title: 'Get Matched', description: 'We match you with an available unit that fits your requirements' },
    { number: '3', icon: CheckCircle, title: 'Move In Ready', description: 'Arrive to a fully furnished, professionally cleaned home from day one' }
  ];

  return (
    <div className="book-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <a href="/" className="logo-section">
            <HouseHeart className="logo-icon" />
            <span className="logo-text">Envoy Stays</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="main-nav">
            <a href="#amenities">What's Included</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#inquiry-form">
              <Button variant="default" size="sm">Request a Stay</Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <a href="#amenities" onClick={() => setMobileMenuOpen(false)}>What's Included</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#inquiry-form" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="default" size="sm" className="w-full">Request a Stay</Button>
            </a>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="book-hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-headline">
              Premium Furnished Housing for Traveling Professionals
            </h1>
            <p className="hero-subheadline">
              Move-in ready apartments for nurses, consultants, executives, and contractors. 
              Fully furnished, professionally managed, and designed for extended stays.
            </p>
            <div className="hero-cta">
              <Button 
                size="lg" 
                className="cta-primary"
                onClick={() => document.getElementById('inquiry-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Request a Stay
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section id="amenities" className="amenities-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">What's Included</h2>
            <p className="section-subtitle">
              Every Envoy Stays unit comes fully equipped so you can focus on your work, not setting up a home.
            </p>
          </div>
          <div className="amenities-grid">
            {amenities.map((amenity, index) => {
              const IconComponent = amenity.icon;
              return (
                <div key={index} className="amenity-card">
                  <div className="amenity-icon">
                    <IconComponent size={28} />
                  </div>
                  <div className="amenity-content">
                    <h3 className="amenity-title">{amenity.title}</h3>
                    <p className="amenity-description">{amenity.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              From inquiry to move-in, we make the process simple and stress-free.
            </p>
          </div>
          <div className="steps-grid">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="step-card">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry-form" className="inquiry-section">
        <div className="section-container">
          <div className="inquiry-wrapper">
            <div className="inquiry-header">
              <h2 className="section-title">Request a Stay</h2>
              <p className="section-subtitle">
                Tell us about your housing needs and we'll get back to you within 24 hours.
              </p>
            </div>
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="location">Desired Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    placeholder="City or area"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="moveInDate">Move-in Date *</Label>
                  <Input
                    id="moveInDate"
                    name="moveInDate"
                    type="date"
                    value={formData.moveInDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="lengthOfStay">Length of Stay *</Label>
                  <Input
                    id="lengthOfStay"
                    name="lengthOfStay"
                    type="text"
                    placeholder="e.g., 3 months, 6 weeks"
                    value={formData.lengthOfStay}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="Any specific requirements, preferences, or questions..."
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                />
              </div>
              <Button type="submit" size="lg" className="submit-button">
                <Send size={18} />
                Request a Stay
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer book-footer">
        <div className="footer-container">
          <div className="footer-bottom">
            <div className="footer-brand-simple">
              <HouseHeart className="logo-icon" size={20} />
              <span className="logo-text-small">Envoy Stays</span>
            </div>
            <p>&copy; 2026 Envoy Stays. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookPage;
